import { formatHours, isHoursUnit } from "./format-hours";
import { tooltipLine } from "./comparation-tooltip";

/**
 * Opções do Highcharts para séries com unidade `hours`.
 *
 * As visualizações que vêm do back trazem `labels.format` fixo (ex.: "{value}") no eixo e, às
 * vezes, `dataLabels.format` / `tooltip.pointFormat`. No Highcharts 11 o `dataLabels.format`
 * vence o `formatter` e o `pointFormatter` vence o `pointFormat`; no eixo, o `formatter` vence.
 * Para não depender disso, onde a saída tem de ser `H:MM` o `format` sai e entra o `formatter`.
 *
 * - eixo: só quando TODAS as séries daquele eixo são `hours`. Eixo com unidades misturadas fica
 *   como veio;
 * - dataLabels e tooltip: por série, sempre que a série é `hours`, misturada ou não.
 */

const axisIndexOf = (serie, yAxisList) => {
    const ref = serie?.yAxis;
    if (ref === undefined || ref === null) return 0;
    if (typeof ref === "number") return ref;
    const byId = yAxisList.findIndex((axis) => axis?.id === ref);
    if (byId >= 0) return byId;
    const asNumber = Number(ref);
    return Number.isInteger(asNumber) ? asNumber : -1;
};

/**
 * Índices dos eixos em que todas as séries são `hours`.
 * @param {{ series: Array, yAxis: Object|Array }} param0
 * @returns {Set<Number>}
 */
export const hoursAxisIndexes = ({ series = [], yAxis } = {}) => {
    const yAxisList = Array.isArray(yAxis) ? yAxis : [yAxis || {}];
    const unitsByAxis = new Map();

    series
        .filter((serie) => serie && !serie.key)
        .forEach((serie) => {
            const index = axisIndexOf(serie, yAxisList);
            if (index < 0) return;
            if (!unitsByAxis.has(index)) unitsByAxis.set(index, []);
            unitsByAxis.get(index).push(serie.unit);
        });

    const indexes = new Set();
    unitsByAxis.forEach((units, index) => {
        if (units.length && units.every(isHoursUnit)) indexes.add(index);
    });
    return indexes;
};

export const withHoursAxisLabels = (axis = {}) => {
    const labels = { ...(axis.labels || {}) };
    delete labels.format;
    labels.formatter = function () {
        return formatHours(this.value);
    };
    return { ...axis, labels };
};

const hoursDataLabels = (dataLabels = {}) => {
    const formatter = function () {
        return formatHours(this.y);
    };
    if (Array.isArray(dataLabels))
        return dataLabels.map((item) => ({
            ...item,
            format: undefined,
            formatter
        }));
    // `format: undefined` explícito: o merge do Highcharts copia e anula o format do plotOptions
    return { ...dataLabels, format: undefined, formatter };
};

// `pointFormatter` da série vence o `pointFormat` (texto) que vem no config do back. Um
// `tooltip.formatter` do gráfico continua vencendo os dois — e esses já formatam pela unidade.
const hoursTooltip = (tooltip = {}) => ({
    ...tooltip,
    pointFormatter: function () {
        return tooltipLine(this.series, this.y);
    }
});

export const withHoursSerieLabels = (serie) => {
    if (!serie || !isHoursUnit(serie.unit)) return serie;
    return {
        ...serie,
        dataLabels: hoursDataLabels(serie.dataLabels),
        tooltip: hoursTooltip(serie.tooltip)
    };
};

/**
 * Aplica o `H:MM` nos eixos (só os 100% `hours`) e nas séries `hours` (dataLabels e tooltip).
 * Não muda nada quando não há série `hours`.
 * @param {{ series: Array, yAxis: Object|Array }} param0
 * @returns {{ series: Array, yAxis: Object|Array }}
 */
export const applyHoursToChart = ({ series = [], yAxis } = {}) => {
    if (!Array.isArray(series) || !series.some((s) => isHoursUnit(s?.unit)))
        return { series, yAxis };

    const indexes = hoursAxisIndexes({ series, yAxis });

    let newYAxis = yAxis;
    if (Array.isArray(yAxis))
        newYAxis = yAxis.map((axis, index) =>
            indexes.has(index) ? withHoursAxisLabels(axis) : axis
        );
    else if (indexes.has(0)) newYAxis = withHoursAxisLabels(yAxis || {});

    return {
        series: series.map(withHoursSerieLabels),
        yAxis: newYAxis
    };
};
